// src/components/DashboardPage.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AddTransactionForm from "./AddTransactionForm";
import categories from "../data/categories";
import "./Dashboard.css";
import { API_BASE } from "../config";

// ✅ PDF export
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

/**
 * DashboardPage (COMPLETE + FIXED)
 * ✅ Export PDF: SOLO KPI + Tabella "Riepilogo per Colore" per anno selezionato
 * ✅ NIENTE DOPPIONI in dashboard: l'area PDF resta OFFSCREEN
 * ✅ Fix pagina bianca: in export clono l’area e la renderizzo “offscreen ma paintabile”
 * ✅ Footer "Generato da..." visibile SOLO nel PDF
 */

function parseAmount(v) {
  if (v == null) return 0;
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;

  const s = String(v).trim();
  if (!s) return 0;

  const hasDot = s.includes(".");
  const hasComma = s.includes(",");

  // formato europeo "1.234,56"
  if (hasDot && hasComma) {
    const norm = s.replace(/\./g, "").replace(",", ".");
    const n = Number(norm);
    return Number.isFinite(n) ? n : 0;
  }

  // solo virgola "1234,56"
  if (hasComma && !hasDot) {
    const n = Number(s.replace(",", "."));
    return Number.isFinite(n) ? n : 0;
  }

  // default "1234.56"
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}

function safeDateToMs(v) {
  const ms = new Date(v).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

function toDateLabelIt(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return new Intl.DateTimeFormat("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(d);
}

async function readErrorMessage(res) {
  const ct = res.headers.get("content-type") || "";
  if (ct.includes("application/json")) {
    const data = await res.json().catch(() => null);
    return data?.message || data?.error || `Errore HTTP ${res.status}`;
  }
  const text = await res.text().catch(() => "");
  return text || `Errore HTTP ${res.status}`;
}

export default function DashboardPage({ token, user }) {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // ✅ area “PDF base” (non visibile a schermo)
  const exportRef = useRef(null);
  const [pdfExporting, setPdfExporting] = useState(false);

  const eur = useMemo(
    () =>
      new Intl.NumberFormat("it-IT", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        useGrouping: true,
      }),
    []
  );

  const fmtEUR = useCallback((v) => eur.format(parseAmount(v)), [eur]);

  // lookup categoria -> meta
  const categoryMap = useMemo(() => {
    const map = new Map();
    (Array.isArray(categories) ? categories : []).forEach((c) => {
      map.set(c.name, {
        color: c.color,
        label: c.label || c.name,
        name: c.name,
        type: c.type,
      });
    });
    return map;
  }, []);

  const getCategoryMeta = useCallback(
    (categoryName) => {
      const m = categoryMap.get(categoryName);
      if (m) return m;
      return { color: "#999999", label: "Senza categoria", name: categoryName || "" };
    },
    [categoryMap]
  );

  // -------- fetch transactions --------
  const fetchTransactions = useCallback(async () => {
    if (!token) return;

    const controller = new AbortController();
    setLoading(true);
    setError("");

    try {
      const res = await fetch(`${API_BASE}/api/transactions`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
        signal: controller.signal,
      });

      if (!res.ok) throw new Error(await readErrorMessage(res));

      const data = await res.json().catch(() => []);
      setTransactions(Array.isArray(data) ? data : []);
    } catch (e) {
      if (e?.name === "AbortError") return;
      setTransactions([]);
      setError(e?.message || "Errore nel recupero delle transazioni");
    } finally {
      setLoading(false);
    }

    return () => controller.abort();
  }, [token]);

  useEffect(() => {
    if (!token) return;
    let cleanup = null;

    (async () => {
      cleanup = await fetchTransactions();
    })();

    return () => {
      if (typeof cleanup === "function") cleanup();
    };
  }, [token, fetchTransactions]);

  // -------- years --------
  const availableYears = useMemo(() => {
    const years = new Set();
    for (const t of transactions) {
      const ms = safeDateToMs(t?.data);
      if (!ms) continue;
      years.add(new Date(ms).getFullYear());
    }

    if (years.size === 0) {
      const now = new Date().getFullYear();
      return [now, now - 1, now - 2, now - 3];
    }

    return Array.from(years).sort((a, b) => b - a);
  }, [transactions]);

  useEffect(() => {
    if (!availableYears.length) return;
    if (!availableYears.includes(selectedYear)) setSelectedYear(availableYears[0]);
  }, [availableYears, selectedYear]);

  const transactionsForSelectedYear = useMemo(() => {
    return transactions
      .filter((t) => new Date(t?.data).getFullYear() === selectedYear)
      .slice()
      .sort((a, b) => safeDateToMs(b?.data) - safeDateToMs(a?.data));
  }, [transactions, selectedYear]);

  // -------- KPI --------
  const entrateTotali = useMemo(() => {
    let sum = 0;
    for (const t of transactionsForSelectedYear) {
      const imp = parseAmount(t?.importo);
      if (imp >= 0) sum += imp;
    }
    return sum;
  }, [transactionsForSelectedYear]);

  const usciteTotali = useMemo(() => {
    let sum = 0;
    for (const t of transactionsForSelectedYear) {
      const imp = parseAmount(t?.importo);
      if (imp < 0) sum += Math.abs(imp);
    }
    return sum;
  }, [transactionsForSelectedYear]);

  const profitto = useMemo(() => entrateTotali - usciteTotali, [entrateTotali, usciteTotali]);

  // -------- summary by color --------
  const summaryByColor = useMemo(() => {
    const summary = {};

    (Array.isArray(categories) ? categories : []).forEach((cat) => {
      if (!summary[cat.color]) {
        summary[cat.color] = {
          color: cat.color,
          label: cat.label || cat.name,
          entrate: 0,
          uscite: 0,
          categories: [],
        };
      }
      if (!summary[cat.color].categories.includes(cat.name)) {
        summary[cat.color].categories.push(cat.name);
      }
    });

    for (const t of transactionsForSelectedYear) {
      const meta = getCategoryMeta(t?.categoria);
      const color = meta.color;
      const label = meta.label;

      if (!summary[color]) {
        summary[color] = { color, label, entrate: 0, uscite: 0, categories: [] };
      }

      if (t?.categoria && !summary[color].categories.includes(t.categoria)) {
        summary[color].categories.push(t.categoria);
      }

      const imp = parseAmount(t?.importo);
      if (imp >= 0) summary[color].entrate += imp;
      else summary[color].uscite += Math.abs(imp);
    }

    return Object.values(summary).sort((a, b) =>
      String(a.label || "").localeCompare(String(b.label || ""), "it")
    );
  }, [transactionsForSelectedYear, getCategoryMeta]);

  const recentTx = useMemo(
    () => transactionsForSelectedYear.slice(0, 5),
    [transactionsForSelectedYear]
  );

  // -------- modal ESC close --------
  useEffect(() => {
    if (!isModalOpen) return;
    const onKeyDown = (e) => e.key === "Escape" && setModalOpen(false);
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isModalOpen]);

  // -------- add callback --------
  const handleAdd = useCallback((tx) => {
    setTransactions((prev) => [...prev, tx]);
    setModalOpen(false);
  }, []);

  // ✅ EXPORT PDF (FIX pagina bianca)
  const exportPdf = useCallback(async () => {
    const node = exportRef.current;
    if (!node) return;

    setPdfExporting(true);
    setError("");

    const nextFrame = () => new Promise((r) => requestAnimationFrame(() => r()));

    let holder = null;

    try {
      // (best-effort) aspetta font
      try {
        if (document?.fonts?.ready) await document.fonts.ready;
      } catch {}

      // 1) Clona l’area PDF (così non tocchiamo layout/visibilità)
      const clone = node.cloneNode(true);

      // 2) Contenitore offscreen ma RENDERIZZABILE
      holder = document.createElement("div");
      holder.setAttribute("aria-hidden", "true");
      holder.style.position = "fixed";
      holder.style.left = "-10000px";
      holder.style.top = "0";
      holder.style.zIndex = "-1";
      holder.style.pointerEvents = "none";
      holder.style.background = "#ffffff";

      // imposta una width sensata per evitare tagli strani
      const w = Math.max(node.scrollWidth || 0, node.clientWidth || 0, 1100);
      holder.style.width = `${w}px`;

      // IMPORTANT: niente display:none / visibility:hidden
      clone.style.display = "block";
      clone.style.visibility = "visible";
      clone.style.opacity = "1";
      clone.style.background = "#ffffff";

      // attiva “rendering” SOLO nel clone (footer visibile nel PDF)
      clone.classList.add("pdf-export-area--rendering");

      holder.appendChild(clone);
      document.body.appendChild(holder);

      // 3) Attendi 2 frame per layout/stili
      await nextFrame();
      await nextFrame();

      // 4) Canvas
      const canvas = await html2canvas(clone, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        logging: false,
        windowWidth: Math.max(clone.scrollWidth, clone.clientWidth),
        windowHeight: Math.max(clone.scrollHeight, clone.clientHeight),
      });

      const imgData = canvas.toDataURL("image/png");

      // 5) PDF + paginazione verticale
      const pdf = new jsPDF("p", "mm", "a4");
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();

      const imgW = pageW;
      const imgH = (canvas.height * imgW) / canvas.width;

      let y = 0;
      let remaining = imgH;

      while (remaining > 0) {
        pdf.addImage(imgData, "PNG", 0, y, imgW, imgH);
        remaining -= pageH;
        if (remaining > 0) {
          pdf.addPage();
          y -= pageH;
        }
      }

      pdf.save(`riepilogo_per_colore_${selectedYear}.pdf`);
    } catch (e) {
      console.error(e);
      setError("Errore durante l’esportazione PDF (pagina bianca).");
    } finally {
      try {
        if (holder && holder.parentNode) holder.parentNode.removeChild(holder);
      } catch {}
      setPdfExporting(false);
    }
  }, [selectedYear]);

  return (
    <div className="dashboard">
      {!token ? (
        <div className="alert alert--info" role="status">
          Per visualizzare la dashboard effettua l’accesso.
        </div>
      ) : loading ? (
        <p className="dashLoading">Caricamento...</p>
      ) : (
        <div className="page">
          {/* top actions */}
          <div className="dashTopActions">
            <button className="open-modal-btn" type="button" onClick={() => setModalOpen(true)}>
              Nuova Transazione
            </button>

            <button className="btn btn--ghost" type="button" onClick={fetchTransactions}>
              Aggiorna
            </button>
          </div>

          {error && (
            <div className="alert alert--error" role="alert" style={{ marginBottom: 12 }}>
              {error}
            </div>
          )}

          {/* HEADER + anno + pulsante PDF */}
          <div className="section-head" style={{ alignItems: "center" }}>
            <h2 className="section-title" style={{ margin: 0 }}>
              Riepilogo per Colore
            </h2>

            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <div className="year-select">
                <label htmlFor="year-select">Anno corrente</label>
                <select
                  id="year-select"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  aria-label="Seleziona anno per visualizzare le transazioni"
                >
                  {availableYears.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>

              <button
                className="btn btn--primary"
                type="button"
                onClick={exportPdf}
                disabled={pdfExporting}
                title="Esporta solo KPI + riepilogo per colore in PDF"
              >
                {pdfExporting ? "Esportazione..." : "📄 Esporta PDF"}
              </button>
            </div>
          </div>

          {/* KPI visibili UNA sola volta */}
          <div className="kpi-row">
            <div className="kpi-card kpi--green">
              <div className="kpi-icon">💰</div>
              <h3 className="kpi-title">Entrate Totali</h3>
              <div className="kpi-value">{fmtEUR(entrateTotali)}</div>
            </div>

            <div className="kpi-card kpi--pink">
              <div className="kpi-icon">💸</div>
              <h3 className="kpi-title">Uscite Totali</h3>
              <div className="kpi-value">{fmtEUR(usciteTotali)}</div>
            </div>

            <div className="kpi-card kpi--indigo">
              <div className="kpi-icon">📈</div>
              <h3 className="kpi-title">Profitto</h3>
              <div className="kpi-value">{fmtEUR(profitto)}</div>
            </div>
          </div>

          {/* SUMMARY MOBILE */}
          <div className="dashMobileList" role="list" aria-label="Riepilogo per colore (mobile)">
            {summaryByColor.map((s) => {
              const saldo = parseAmount(s.entrate) - parseAmount(s.uscite);
              const saldoClass = saldo < 0 ? "is-negative" : "is-positive";

              return (
                <div key={s.color} className="dashCard" role="listitem">
                  <div className="dashCardTop">
                    <div className="dashColor">
                      <span className="color-chip" style={{ backgroundColor: s.color }} />
                      <span className="dashColorLabel">{s.label || ""}</span>
                    </div>
                    <div className={`dashSaldo ${saldoClass}`}>{fmtEUR(saldo)}</div>
                  </div>

                  <div className="dashCardMeta">
                    <div className="dashMetaRow">
                      <span className="dashMetaLabel">Categorie</span>
                      <span className="dashMetaValue">{(s.categories || []).join(", ") || "—"}</span>
                    </div>

                    <div className="dashMetaGrid">
                      <div className="dashMiniKpi">
                        <div className="dashMiniLabel">Entrate</div>
                        <div className="dashMiniValue is-positive">{fmtEUR(s.entrate)}</div>
                      </div>
                      <div className="dashMiniKpi">
                        <div className="dashMiniLabel">Uscite</div>
                        <div className="dashMiniValue is-negative">{fmtEUR(s.uscite)}</div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* SUMMARY DESKTOP */}
          <div className="table-block dashDesktopOnly">
            <table className="summary-table">
              <thead>
                <tr>
                  <th className="t-center">Colore</th>
                  <th>Categoria/e</th>
                  <th className="t-right">Entrate</th>
                  <th className="t-right">Uscite</th>
                  <th className="t-right">Saldo</th>
                </tr>
              </thead>
              <tbody>
                {summaryByColor.map((s) => {
                  const saldo = parseAmount(s.entrate) - parseAmount(s.uscite);
                  return (
                    <tr key={s.color}>
                      <td className="t-center" style={{ whiteSpace: "nowrap" }}>
                        <span className="color-chip" style={{ backgroundColor: s.color }} />
                        <span style={{ marginLeft: 8, fontWeight: 800 }}>{s.label || ""}</span>
                      </td>
                      <td>{(s.categories || []).join(", ")}</td>
                      <td className="t-right">{fmtEUR(s.entrate)}</td>
                      <td className="t-right">{fmtEUR(s.uscite)}</td>
                      <td className="t-right">{fmtEUR(saldo)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Recent */}
          <h3 className="section-mini">Transazioni Recenti</h3>

          {/* RECENT MOBILE */}
          <div className="dashMobileList" role="list" aria-label="Transazioni recenti (mobile)">
            {recentTx.map((t) => {
              const imp = parseAmount(t?.importo);
              const neg = imp < 0;

              const meta = getCategoryMeta(t?.categoria);
              const dotColor = t?.coloreCategoria || meta.color;

              return (
                <div key={t.id} className="dashCard dashTxCard" role="listitem">
                  <div className="dashCardTop">
                    <div className="dashTxDate">{toDateLabelIt(t?.data)}</div>
                    <div className={`dashTxAmount ${neg ? "is-negative" : "is-positive"}`}>
                      {fmtEUR(imp)}
                    </div>
                  </div>

                  <div className="dashTxDesc">{t?.descrizione || "—"}</div>

                  <div className="dashTxMeta">
                    <span className="dashChip">
                      <span className="category-dot" style={{ backgroundColor: dotColor }} />
                      <span className="dashChipText">{t?.categoria || "—"}</span>
                    </span>
                    <span className="dashChip dashChip--muted">
                      <span className="dashChipText">{t?.tipo || (imp < 0 ? "Uscita" : "Entrata")}</span>
                    </span>
                  </div>
                </div>
              );
            })}

            {transactionsForSelectedYear.length === 0 ? (
              <div className="dashEmpty">Nessuna transazione per l’anno selezionato.</div>
            ) : null}
          </div>

          {/* RECENT DESKTOP */}
          <div className="table-block dashDesktopOnly">
            <table className="transaction-table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Descrizione</th>
                  <th className="t-right">Importo</th>
                  <th>Tipo</th>
                  <th>Categoria</th>
                </tr>
              </thead>
              <tbody>
                {recentTx.map((t) => {
                  const imp = parseAmount(t?.importo);
                  const meta = getCategoryMeta(t?.categoria);
                  const dotColor = t?.coloreCategoria || meta.color;

                  return (
                    <tr key={t.id} className={imp < 0 ? "negative" : "positive"}>
                      <td>{toDateLabelIt(t?.data)}</td>
                      <td>{t?.descrizione || "—"}</td>
                      <td className="t-right">{fmtEUR(imp)}</td>
                      <td>{t?.tipo || (imp < 0 ? "Uscita" : "Entrata")}</td>
                      <td>
                        <span className="category-dot" style={{ backgroundColor: dotColor }} />
                        {t?.categoria || "—"}
                      </td>
                    </tr>
                  );
                })}

                {transactionsForSelectedYear.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ color: "var(--tx-muted)" }}>
                      Nessuna transazione per l’anno selezionato.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          {/* MODALE */}
          {isModalOpen && (
            <div
              className="modal-overlay"
              role="dialog"
              aria-modal="true"
              aria-label="Nuova transazione"
              onMouseDown={(e) => {
                if (e.target === e.currentTarget) setModalOpen(false);
              }}
            >
              <div className="modal-content modal-content--responsive" onMouseDown={(e) => e.stopPropagation()}>
                <div className="modal-head">
                  <div className="modal-head__titles">
                    <h3 className="modal-title">Aggiungi Transazione</h3>
                    <p className="modal-subtitle">Compila i campi e salva.</p>
                  </div>

                  <button
                    type="button"
                    className="modal-closeX"
                    onClick={() => setModalOpen(false)}
                    aria-label="Chiudi"
                    title="Chiudi"
                  >
                    ✕
                  </button>
                </div>

                <div className="modal-body">
                  <AddTransactionForm onAdd={handleAdd} token={token} user={user} />
                </div>

                <div className="modal-footer">
                  <button className="btn btn--ghost" type="button" onClick={() => setModalOpen(false)}>
                    Chiudi
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ✅ AREA PDF BASE: resta offscreen e NON deve essere catturata direttamente */}
          <div ref={exportRef} className="pdf-export-area pdf-export-area--offscreen" aria-hidden="true">
            {/* KPI */}
            <div className="kpi-row">
              <div className="kpi-card kpi--green">
                <div className="kpi-icon">💰</div>
                <h3 className="kpi-title">Entrate Totali</h3>
                <div className="kpi-value">{fmtEUR(entrateTotali)}</div>
              </div>

              <div className="kpi-card kpi--pink">
                <div className="kpi-icon">💸</div>
                <h3 className="kpi-title">Uscite Totali</h3>
                <div className="kpi-value">{fmtEUR(usciteTotali)}</div>
              </div>

              <div className="kpi-card kpi--indigo">
                <div className="kpi-icon">📈</div>
                <h3 className="kpi-title">Profitto</h3>
                <div className="kpi-value">{fmtEUR(profitto)}</div>
              </div>
            </div>

            {/* Header PDF */}
            <div className="pdf-export-head">
              <div className="pdf-export-title">Riepilogo per Colore</div>
              <div className="pdf-export-year">Anno corrente: {selectedYear}</div>
            </div>

            {/* Tabella PDF */}
            <div className="table-block">
              <table className="summary-table">
                <thead>
                  <tr>
                    <th className="t-center">Colore</th>
                    <th>Categoria/e</th>
                    <th className="t-right">Entrate</th>
                    <th className="t-right">Uscite</th>
                    <th className="t-right">Saldo</th>
                  </tr>
                </thead>
                <tbody>
                  {summaryByColor.map((s) => {
                    const saldo = parseAmount(s.entrate) - parseAmount(s.uscite);
                    return (
                      <tr key={`${s.color}-${s.label}`}>
                        <td className="t-center" style={{ whiteSpace: "nowrap" }}>
                          <span className="color-chip" style={{ backgroundColor: s.color }} />
                          <span style={{ marginLeft: 8, fontWeight: 800 }}>{s.label || ""}</span>
                        </td>
                        <td>{(s.categories || []).join(", ")}</td>
                        <td className="t-right">{fmtEUR(s.entrate)}</td>
                        <td className="t-right">{fmtEUR(s.uscite)}</td>
                        <td className="t-right">{fmtEUR(saldo)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Footer SOLO PDF: verrà mostrato nel clone grazie a .pdf-export-area--rendering */}
            <div className="pdf-export-footer pdf-only">
              Generato da SOSControl • {new Date().toLocaleString("it-IT")}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
